import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { useMount } from "react-use";
import { Paper, Portal } from "@activecollab/components";
import { EmptySlate } from "@activecollab/components";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import { useStateWithCallbackLazy } from "use-state-with-callback";
import { useInfiniteScroll } from "~/components/Hooks";
import usePagination from "~/components/Hooks/usePagination";
import SectionLoader from "~/components/SectionLoader/SectionLoader";
import TasksArchiveFilter from "~/pages/ProjectTasksArchive/TasksArchiveFilter";
import TasksArchiveItem from "~/pages/ProjectTasksArchive/TasksArhiveItem";
import { urlParamsToSelection } from "~/pages/ProjectTasksArchive/urlParamsToSelection";
import { FETCH_TASKS_ARCHIVE_SUCCESS } from "~/store/action_types";
import { fetchTasksArchiveForProject, flushTasksArchiveForProject } from "~/store/actions/actions.tasks";
import { makeGetFilteredTasksArchiveByProjectId } from "~/store/selectors/selector.tasks";
import Angie from "~/utils/angie";

const TasksArchiveItems = ({ filterContainer, onLoadingStateChange, onCountersChange }) => {
  const { t } = useTranslation();
  const { projectId: _projectId } = useParams();
  const projectId = useMemo(() => Number(_projectId), [_projectId]);
  const dispatch = useDispatch();

  const itemsPerPage = 50;
  const [headers, setHeaders] = useStateWithCallbackLazy({});
  const pagination = usePagination(headers);
  const [searchParams] = useSearchParams();

  const [delayedSelected, setDelayedSelected] = useState();
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [isPageLoadedAtLeastOnce, setIsPageLoadedAtLeastOnce] = useState(false);
  const [isLoadingWholePage, setIsLoadingWholePage] = useState(true);
  const [isLoadingPagination, setIsLoadingPagination] = useState(false);

  const selected = useMemo(() => {
    return urlParamsToSelection(searchParams);
  }, [searchParams]);

  const getFilteredTasksArchiveByProjectId = useMemo(() => makeGetFilteredTasksArchiveByProjectId(), []);

  const resetPagination = useCallback(() => {
    setHeaders({
      "x-angie-paginationnextcursor": "last",
    });
  }, [setHeaders]);

  const handleFetchAction = useCallback(
    (action) => {
      if (action.type === FETCH_TASKS_ARCHIVE_SUCCESS) {
        setHeaders(action.responseHeaders ? action.responseHeaders : {}, () => {
          setIsLoadingPagination(false);
        });
      }

      if (onCountersChange && action.responseHeaders) {
        const taskCount = action.responseHeaders["x-angie-totalcompletedtaskcount"] || 0;
        const taskListCount = action.responseHeaders["x-angie-totalcompletedtasklistcount"] || 0;

        onCountersChange(taskCount, taskListCount);
      }
    },
    [setHeaders, onCountersChange]
  );

  const loadFromTheBeginning = useCallback(async () => {
    // no change in selection, no need to reload from the beginning
    if (delayedSelected && isEqual(selected, delayedSelected)) {
      return true;
    }

    setIsLoadingWholePage(true);
    resetPagination();

    const action = await dispatch(
      fetchTasksArchiveForProject(
        projectId,
        {
          limit: itemsPerPage,
          cursor: "last",
        },
        selected
      )
    );
    setDelayedSelected({ ...selected });
    handleFetchAction(action);

    setIsLoadingWholePage(false);
    setIsPageLoadedAtLeastOnce(true);
  }, [
    dispatch,
    resetPagination,
    projectId,
    handleFetchAction,
    setIsLoadingWholePage,
    setIsPageLoadedAtLeastOnce,
    selected,
    setDelayedSelected,
    delayedSelected,
  ]);

  const handleLoadMore = useCallback(async () => {
    if (pagination.cursor && !isLoadingPagination) {
      setIsLoadingPagination(true);
      const action = await dispatch(
        fetchTasksArchiveForProject(
          projectId,
          {
            cursor: pagination.cursor,
            limit: itemsPerPage,
          },
          selected
        )
      );

      setDelayedSelected({ ...selected });
      handleFetchAction(action);
    }
  }, [
    pagination.cursor,
    isLoadingPagination,
    dispatch,
    itemsPerPage,
    projectId,
    handleFetchAction,
    selected,
    setDelayedSelected,
  ]);

  useInfiniteScroll(handleLoadMore, 500, null);

  useEffect(() => {
    loadFromTheBeginning();
  }, [selected, loadFromTheBeginning]);

  const filteringActive = useMemo(() => {
    return Boolean(
      selected?.taskListFilter?.length ||
        selected?.assigneeFilter?.length ||
        selected?.labelFilter?.length ||
        selected?.dueDateFilter?.length ||
        selected?.completionDateFilter?.length ||
        selected?.completedByFilter?.length
    );
  }, [selected]);

  const items = useSelector((state) => {
    return getFilteredTasksArchiveByProjectId(state, projectId, delayedSelected, pagination.boundaryCursor);
  });

  useEffect(() => {
    onLoadingStateChange && onLoadingStateChange(isLoadingWholePage);
  }, [onLoadingStateChange, isLoadingWholePage]);

  useMount(async () => {
    setIsComponentMounted(true);
    await dispatch(flushTasksArchiveForProject(projectId));
  });

  return (
    <Paper className="tw-flex-1 tw-relative tw-mb-8 tw-p-8">
      <div className="tw-flex tw-flex-col tw-h-full tw-gap-6">
        {isLoadingWholePage &&
          [1, 2, 3, 4, 5, 6].map((i) => {
            return (
              <TasksArchiveItem
                key={i}
                loading={true}
              />
            );
          })}

        {!isLoadingWholePage && (!items || items?.length < 1) && (
          <EmptySlate
            title={!filteringActive ? t("No Completed Tasks") : t("No Filter Matches")}
            className="tw-h-full"
            description={
              !filteringActive
                ? t("There are no completed tasks at the moment.")
                : t("Adjust the filters to see different results.")
            }
            src={
              !filteringActive
                ? `${Angie.assets_url}/system/images/layout/tasks_archive_empty_page.png`
                : `${Angie.assets_url}/environment/images/layout/search_empty_page.png`
            }
            alt="Empty Tasks Archive"
          />
        )}

        {!isLoadingWholePage && items?.length ? (
          <>
            {items.map((item) => {
              return (
                <TasksArchiveItem
                  item={item}
                  key={`${item.id}-${item.class}`}
                />
              );
            })}

            {pagination.cursor && (
              <div className="tw-h-6 tw-mt-10 tw-mb-10">
                {isLoadingPagination && <SectionLoader className="tw-flex tw-flex-1 tw-h-auto tw-py-1" />}
              </div>
            )}
          </>
        ) : null}
      </div>

      {isComponentMounted && (
        <Portal container={document.getElementById(filterContainer)}>
          <div
            className={classNames({
              "tw-hidden": isLoadingWholePage && !isPageLoadedAtLeastOnce,
            })}
          >
            <TasksArchiveFilter />
          </div>
        </Portal>
      )}
    </Paper>
  );
};

TasksArchiveItems.displayName = "TasksArchiveItems";
TasksArchiveItems.propTypes = {
  filterContainer: PropTypes.string,
  onLoadingStateChange: PropTypes.func,
  onCountersChange: PropTypes.func,
};

export default TasksArchiveItems;
